import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import LanguageSelector from "@/components/LanguageSelector";
import ThemeToggle from "@/components/ThemeToggle";

/**
 * Guarantees a language switcher on EVERY page — including standalone pages
 * (pricing, legal, login/register, compare, api-docs, public shares) that
 * don't go through PageLayout and never had one.
 *
 * Rather than editing a dozen heterogeneous page headers, this renders a single
 * fixed switcher that shows ONLY when the current page has no inline selector.
 * It detects an inline one by the `[data-testid="language-selector"]` that
 * PageLayout / LandingPage / the dashboard header render; its own selector uses
 * `testId="language-fab"` so it never detects itself. Net result: exactly one
 * switcher per page, and any future page is covered automatically.
 *
 * The check re-runs on every route change (useLocation dependency). Default is
 * hidden, so a page that already has an inline selector never flashes a second
 * one; a page that needs the fab shows it a tick after mount (imperceptible).
 * Positioned bottom-right to clear the left-anchored InstallPWA prompt and the
 * top navs.
 */
const GlobalLanguageFab = () => {
  const location = useLocation();
  const [show, setShow] = useState(false);

  useEffect(() => {
    // Detect an inline selector and hide the fab when one exists. A one-shot
    // check after paint is NOT enough: the dashboard renders its header
    // selector only after auth/data load (a loading screen shows first), so an
    // early check finds none and the fab wrongly stays — showing TWO switchers.
    // A MutationObserver re-checks whenever the DOM changes, so the fab hides
    // the moment an async header selector mounts (and reappears if it unmounts).
    // Checks are coalesced into one rAF so frequent chat DOM updates stay cheap.
    let raf = 0;
    const check = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        setShow(!document.querySelector('[data-testid="language-selector"]'));
      });
    };
    check();
    const observer = new MutationObserver(check);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => {
      cancelAnimationFrame(raf);
      observer.disconnect();
    };
  }, [location.pathname]);

  if (!show) return null;

  return (
    <div
      className="fixed bottom-4 right-4 z-40"
      data-testid="language-fab-wrapper"
    >
      <div className="glass rounded-full shadow-lg border border-white/10 flex items-center pl-1">
        <ThemeToggle className="!w-9 !h-9" />
        <LanguageSelector variant="ghost" testId="language-fab" />
      </div>
    </div>
  );
};

export default GlobalLanguageFab;
